import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import { getCongregations, getHubHospitality, saveHubHospitality } from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];
const DAY_LABELS = {
  'Thu-April-9':  'Thursday, April 9',
  'Fri-April-10': 'Friday, April 10',
  'Sat-April-11': 'Saturday, April 11',
  'Sun-April-12': 'Sunday, April 12',
};
const MEAL_OPTIONS = ['Morning Snacks', 'Lunch', 'Afternoon Snacks', 'Dinner'];

function HubHospitality({ onLogout }) {
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [congregations, setCongregations] = useState([]);
  const [data, setData] = useState(null); // { congregation: '', meals: [], notes: '' }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadCongregations = useCallback(async () => {
    const congs = await getCongregations();
    setCongregations(congs.filter(c => c.active).map(c => c.name).sort());
  }, []);

  const loadDay = useCallback(async (day) => {
    setLoading(true);
    const doc = await getHubHospitality(day);
    setData(doc || { congregation: '', meals: [], notes: '' });
    setLoading(false);
  }, []);

  useEffect(() => { loadCongregations(); }, [loadCongregations]);
  useEffect(() => { loadDay(selectedDay); }, [selectedDay, loadDay]);

  const save = async (updated) => {
    setSaving(true);
    await saveHubHospitality(selectedDay, updated);
    setSaving(false);
  };

  const update = (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    save(updated);
  };

  const toggleMeal = (meal) => {
    const meals = data.meals || [];
    const updated = {
      ...data,
      meals: meals.includes(meal) ? meals.filter(m => m !== meal) : [...meals, meal],
    };
    setData(updated);
    save(updated);
  };

  if (loading) return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading…</div></div>;

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Hospitality</h1>
        <p className="page-subtitle">Track which congregation is providing food and snacks for each event day.</p>
      </header>

      <div className="scheduling-content">
        {/* Day Tabs */}
        <div className="day-selector">
          {DAYS.map(day => (
            <button
              key={day}
              className={`day-tab ${selectedDay === day ? 'active' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              {day.split('-')[0]}
            </button>
          ))}
        </div>

        {saving && <div className="save-indicator">💾 Saving…</div>}

        <h3 style={{ marginTop: '24px', marginBottom: '20px', color: '#1e293b' }}>
          {DAY_LABELS[selectedDay]}
        </h3>

        <div className="location-section" style={{ maxWidth: '560px' }}>
          <div className="shift-header" style={{ padding: '14px 20px', background: '#f8fafc', borderRadius: '8px 8px 0 0' }}>
            <strong>Hospitality Assignment</strong>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px', color: '#374151' }}>
                Congregation Providing Food
              </label>
              <select
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                value={data.congregation || ''}
                onChange={e => update('congregation', e.target.value)}
              >
                <option value="">None assigned yet</option>
                {congregations.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                Meals / Items Provided
              </label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {MEAL_OPTIONS.map(meal => (
                  <label key={meal} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={(data.meals || []).includes(meal)}
                      onChange={() => toggleMeal(meal)}
                    />
                    {meal}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px', color: '#374151' }}>
                Notes
              </label>
              <textarea
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minHeight: '80px', boxSizing: 'border-box', resize: 'vertical' }}
                placeholder="Any special notes about food, timing, setup, etc."
                value={data.notes || ''}
                onChange={e => update('notes', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Summary of all days */}
        <div style={{ marginTop: '32px' }}>
          <h4 style={{ color: '#64748b', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
            All Days Summary
          </h4>
          <AllDaysSummary currentDay={selectedDay} />
        </div>
      </div>
    </div>
  );
}

function AllDaysSummary({ currentDay }) {
  const [summaries, setSummaries] = useState({});

  useEffect(() => {
    const load = async () => {
      const results = {};
      for (const day of ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12']) {
        const doc = await getHubHospitality(day);
        results[day] = doc;
      }
      setSummaries(results);
    };
    load();
  }, [currentDay]);

  const dayLabels = {
    'Thu-April-9': 'Thu Apr 9', 'Fri-April-10': 'Fri Apr 10',
    'Sat-April-11': 'Sat Apr 11', 'Sun-April-12': 'Sun Apr 12',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
      {['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'].map(day => {
        const d = summaries[day];
        return (
          <div key={day} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', fontSize: '13px' }}>
            <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>{dayLabels[day]}</div>
            {d?.congregation
              ? <>
                  <div style={{ color: '#374151' }}>{d.congregation}</div>
                  {d.meals?.length > 0 && <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{d.meals.join(', ')}</div>}
                </>
              : <div style={{ color: '#94a3b8' }}>Not assigned</div>
            }
          </div>
        );
      })}
    </div>
  );
}

export default HubHospitality;
